import {
  IconMountain,
  IconUsers,
  IconBan,
  IconLock,
  IconKey,
  IconUserCheck,
  IconUser,
  IconParkingCircle,
  IconCarGarage,
  IconWalk,
  IconCash,
  IconLockOpen,
  IconShieldHalfFilled,
  IconBackhoe,
  IconArrowBarUp,
  IconForklift,
  IconBulldozer,
  IconTruckLoading,
  IconDisc,
  IconCrane,
  IconBox,
  IconSteeringWheel,
  IconUserCog,
  IconUserBolt,
  IconTruckDelivery,
  IconShovel,
  IconGardenCart,
  IconEngine,
  IconPlant2,
  IconTruck,
  IconTool,
  IconBuildingSkyscraper,
  IconPick,
  IconTractor,
  IconTrees,
  IconRecycle,
  IconAnchor,
  IconRoad,
  IconHammerDrill,
  IconBuildingTunnel,
  IconWheat,
  IconMilk,
  IconAxe,
  IconElevator,
  IconContainer,
  IconTrash,
  IconBolt,
  IconSubmarine,
  IconHammer,
} from '@tabler/icons-react';

export type HeavyDutyMachineryOptionIcon = typeof IconMountain;

// One icon per option value across every HeavyDutyMachinery enum/checklist field
// used by the /explore filters modal -- tile grids show it large, chip rows
// show it small. Falls back to IconCircleDot (see FiltersModal) for any
// value not listed here rather than throwing.
export const HEAVY_DUTY_MACHINERY_OPTION_ICONS: Record<string, HeavyDutyMachineryOptionIcon> = {
  // CATEGORY_OPTIONS
  Excavator: IconBackhoe,
  ScissorLift: IconArrowBarUp,
  // No literal skid-steer/bobcat glyph in this icon set -- IconShovel
  // (scoops/moves material) is the closest available semantic match.
  SkidSteerLoader: IconShovel,
  // No literal tipping dump-truck glyph either -- IconGardenCart (a
  // tipping cart) is the closest available semantic match.
  Dumper: IconGardenCart,
  Telehandler: IconForklift,
  Dozer: IconBulldozer,
  WheelLoader: IconTruckLoading,
  TunnelBoringMachine: IconDisc,
  Crane: IconCrane,
  Other: IconBox,

  // HIRE_MODE_OPTIONS
  DryHireAvailable: IconSteeringWheel,
  WetHireHostOperatorAvailable: IconUserCog,
  WetHireThirdPartyAvailable: IconUserBolt,
  DeliveryAvailable: IconTruckDelivery,

  // ACCESS_TYPE_OPTIONS
  SelfCheckin: IconKey,
  HostGreets: IconUserCheck,
  HostPresentThroughout: IconUsers,
  OnsiteStaff: IconUser,

  // PARKING_OPTIONS
  OnsitePrivate: IconParkingCircle,
  OnsiteDriveIn: IconCarGarage,
  NearbyStreet: IconWalk,
  NearbyPaid: IconCash,
  NoParking: IconBan,

  // CANCELLATION_POLICY_OPTIONS
  Flexible: IconLockOpen,
  Moderate: IconShieldHalfFilled,
  Strict: IconLock,

  // SERVICE_CATEGORY_OPTIONS (see serviceOptions.ts)
  'operacion-maquinaria': IconEngine,
  jardineria: IconPlant2,
  transporte: IconTruck,
  mantenimiento: IconTool,
};

// One icon per Sector (Nivel 1) in the Sector/MachineryFamily/MachineryType
// taxonomy -- see ITaxonomy.ts. Keyed by Code (language-neutral, unaffected
// by which language the taxonomy is currently fetched in), not the
// per-language Slug. Curated at the Sector and MachineryFamily levels only
// (23 icons total between the two maps below); individual leaf
// MachineryTypes (83 of them) render as plain text rows in TaxonomyPicker,
// same "don't show every machine at once" reasoning as the picker's own doc
// comment. Falls back to IconCircleDot for any code not listed here.
export const TAXONOMY_SECTOR_ICONS: Record<string, HeavyDutyMachineryOptionIcon> = {
  'construccion-y-obra-civil': IconBuildingSkyscraper,
  'mineria-y-canteras': IconPick,
  'agricultura-y-ganaderia': IconTractor,
  'forestal-y-madera': IconTrees,
  'elevacion-logistica-y-puertos': IconCrane,
  'residuos-reciclaje-y-energia': IconRecycle,
  'maritimo-y-submarino': IconAnchor,
  'implementos-y-accesorios': IconTool,
};

// One icon per MachineryFamily (Nivel 2) -- see TAXONOMY_SECTOR_ICONS' own doc comment.
export const TAXONOMY_FAMILY_ICONS: Record<string, HeavyDutyMachineryOptionIcon> = {
  'movimiento-de-tierras': IconBulldozer,
  'compactacion-y-pavimentacion': IconRoad,
  'cimentacion-y-perforacion': IconHammerDrill,
  'transporte-de-obra': IconTruck,
  'mineria-a-cielo-abierto': IconMountain,
  'mineria-subterranea': IconBuildingTunnel,
  'agricola-pesada': IconWheat,
  'ganaderia-pesada': IconMilk,
  'maquinaria-forestal-pesada': IconAxe,
  'elevacion-de-carga-y-personal': IconElevator,
  'logistica-portuaria-e-intermodal': IconContainer,
  'reciclaje-y-tratamiento-de-residuos': IconTrash,
  'infraestructura-energetica': IconBolt,
  'dragado-e-intervencion-subacuatica': IconSubmarine,
  'accesorios-hidraulicos-y-mecanicos': IconHammer,
};
